import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Typography,
} from "@mui/material";
import axios from "axios";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getCsrfToken } from "../../utils";

// Applies the "mark as picked up when paid" setting to work orders that were paid earlier.
// The server moves one batch per POST and reports what is left; this dialog repeats the
// call until nothing is. Stopping halfway is harmless: the next run continues from there.
function AutoPickupBackfillDialog({ open, organizationId, onClose, onDone, onError }) {
  const { t } = useTranslation();
  const [count, setCount] = useState(null); // null while the count is loading
  const [moved, setMoved] = useState(0);
  const [running, setRunning] = useState(false);

  const url = `/api/organizations/${organizationId}/auto-pickup-backfill/`;
  const requestConfig = () => ({
    headers: { "X-CSRFToken": getCsrfToken(), "X-Organization-Id": organizationId },
  });
  const errorMessage = (error) => error.response?.data?.detail || t("autoPickup.backfillFailed");

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setCount(null);
    setMoved(0);
    axios
      .get(url, requestConfig())
      .then((response) => {
        if (!cancelled) setCount(response.data.count);
      })
      .catch((error) => {
        if (!cancelled) onError(errorMessage(error));
      });
    return () => {
      cancelled = true;
    };
  }, [open, organizationId]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirm = async () => {
    setRunning(true);
    let total = 0;
    try {
      let remaining = count;
      while (remaining > 0) {
        const { data } = await axios.post(url, null, requestConfig());
        total += data.updated;
        setMoved(total);
        // A batch that moved nothing means there is nothing left to do, whatever it reports.
        remaining = data.updated > 0 ? data.remaining : 0;
      }
      onDone(total);
    } catch (error) {
      console.error("Failed to mark paid work orders as picked up:", error);
      // Batches that went through stay moved; say so instead of a bare "failed".
      onError(total > 0 ? t("autoPickup.backfillInterrupted", { count: total }) : errorMessage(error));
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onClose={running ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t("autoPickup.backfillTitle")}</DialogTitle>
      <DialogContent>
        {count === null && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        )}
        {count === 0 && (
          <Typography variant="body2" data-testid="auto-pickup-backfill-none">
            {t("autoPickup.backfillNone")}
          </Typography>
        )}
        {count > 0 && (
          <>
            <Typography variant="body2" data-testid="auto-pickup-backfill-count">
              {t("autoPickup.backfillBody", { count })}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {t("autoPickup.backfillDetails")}
            </Typography>
            {running && (
              <Box sx={{ mt: 2 }}>
                <LinearProgress variant="determinate" value={Math.min(100, (moved / count) * 100)} />
                <Typography variant="caption" color="text.secondary">
                  {moved} / {count}
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={running}>
          {count > 0 ? t("Cancel") : t("Close")}
        </Button>
        {count > 0 && (
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={running}
            data-testid="auto-pickup-backfill-confirm"
          >
            {running ? <CircularProgress size={20} /> : <span>{t("autoPickup.backfillConfirm")}</span>}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default AutoPickupBackfillDialog;
